import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { CreateSpotTargetRequest, Machine, UpdateSpotTargetRequest } from "@/lib/types";

function invalidatePoolQueries(queryClient: QueryClient) {
  for (const key of [
    "spot-targets",
    "spot-statistics",
    "machines",
    "cbg-machines",
    "target-machines",
    "machine",
  ]) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
}

export function useSpotTargets() {
  return useQuery({
    queryKey: ["spot-targets"],
    queryFn: () => api.listSpotTargets(),
  });
}

export function useCreateSpotTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSpotTargetRequest) => api.createSpotTarget(body),
    onSuccess: () => invalidatePoolQueries(qc),
  });
}

/**
 * Surface a failed spot-target mutation that has no dialog of its own.
 *
 * The enabled switch and Delete fire straight from the card, so without this
 * a 403 is a silent no-op. The usual cause is an admin locking the pool while
 * this tab was open: the access query is fresh for 30s, so the controls
 * outlive the permission. Refetching it makes them disappear.
 */
function useReportMutationError() {
  const queryClient = useQueryClient();
  return (error: Error, action: string) => {
    toast.error(`Failed to ${action}: ${error.message}`);
    if (error.message.startsWith("403:")) {
      void queryClient.invalidateQueries({ queryKey: ["admin-settings", "spot-pool-access"] });
    }
  };
}

export function useUpdateSpotTarget() {
  const qc = useQueryClient();
  const reportError = useReportMutationError();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateSpotTargetRequest & { id: number }) =>
      api.updateSpotTarget(id, body),
    onSuccess: () => invalidatePoolQueries(qc),
    onError: (error) => reportError(error, "update target"),
  });
}

export function useDeleteSpotTarget() {
  const queryClient = useQueryClient();
  const reportError = useReportMutationError();
  return useMutation({
    mutationFn: (id: number) => api.deleteSpotTarget(id),
    onSuccess: () => invalidatePoolQueries(queryClient),
    onError: (error) => reportError(error, "delete target"),
  });
}

export function useEphemeralStartSpotTarget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, durationHours }: { id: number; durationHours: number }) =>
      api.ephemeralStartSpotTarget(id, durationHours),
    onSuccess: () => invalidatePoolQueries(queryClient),
  });
}

export function useEphemeralStopSpotTarget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.ephemeralStopSpotTarget(id),
    onSuccess: () => invalidatePoolQueries(queryClient),
  });
}

export function useTargetMachines(targetId: number, enabled: boolean) {
  return useQuery<Machine[]>({
    queryKey: ["target-machines", targetId],
    queryFn: () => api.listTargetMachines(targetId),
    enabled,
  });
}

export function useAvailableCombos(
  provider: string,
  gpuType: string,
  gpuCount: number,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["available-combos", provider, gpuType, gpuCount],
    queryFn: () => api.getAvailableCombos(provider, gpuType, gpuCount),
    enabled: enabled && Boolean(provider) && Boolean(gpuType) && gpuCount > 0,
  });
}

/** Parse a backend `allowed_combos` string ("eu-north1/fabric-2,...") into a Set. */
export function parseAllowedCombos(raw: string | null | undefined): Set<string> {
  if (!raw) return new Set();
  return new Set(
    raw
      .split(",")
      .map((token) => token.trim())
      .filter(Boolean),
  );
}

/** Format a Set back to the API list shape. */
export function formatAllowedCombos(combos: Set<string>): string[] {
  return Array.from(combos).sort();
}

/** Build the canonical key for a combo (matches the backend storage format). */
export function comboKey(region: string, fabric: string | null): string {
  return fabric ? `${region}/${fabric}` : region;
}
